import { Option } from "@/common/types/option";
import { MissionSchema } from "../schemas/mission.schema";
import {
  Mission,
  MissionReward,
  MissionRewardType,
} from "../types/mission.type";
import { MissionProvider } from "../hooks/useMissionProviders";
import { Product } from "../hooks/useMissionProducts";

export type RewardFormValue = MissionSchema["rewards"][number] & { id: string };

export const generateMissionFieldId = () => {
  const randomGuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return `mission-field-${randomGuid}`;
};

export const missionFormDefaultValues: MissionSchema = {
  mission_type: "establishment",
  establishment: null,
  status: "active",
  name: "",
  description: "",
  allow_multiple_times: false,
  max_times: null,
  minimum_loyalty: "indifferent",
  start_date: "",
  end_date: "",
  restrict_establishments: false,
  allowed_establishments: [],
  conditions: [],
  rewards: [],
};

export const missionRewardTypeLabels: Record<MissionRewardType, string> = {
  product: "Produto",
  points: "Pontos",
  tickets: "Bilhetes",
};

export const mapMissionRewardType = (
  type?: MissionRewardType,
): MissionRewardType => type ?? "product";

export const resolveIncomingRewardType = (
  reward?: MissionReward,
): MissionRewardType => {
  const rawType = reward?.reward_type as MissionRewardType | undefined;
  if (rawType === "points") return "points";
  if (rawType === "product") return "product";
  if (rawType === "tickets") return "tickets";
  if (!reward?.product_id && !reward?.reward_id) return "points";
  return mapMissionRewardType(rawType);
};

export const mapRewardToFormValue = (
  reward?: MissionReward,
): RewardFormValue => {
  const resolvedProductId = reward?.product_id ?? reward?.reward_id ?? undefined;
  const normalizedProductId = resolvedProductId
    ? String(resolvedProductId)
    : undefined;
  const normalizedRewardType = resolveIncomingRewardType(reward);

  return {
    id: reward?.mission_reward_id || reward?.id || generateMissionFieldId(),
    mission_reward_id: reward?.mission_reward_id ?? null,
    pivot_id: reward?.pivot_id ?? null,
    reward_type: normalizedRewardType,
    quantity: Number(reward?.quantity ?? 1),
    reward_id: normalizedProductId,
    product_id: normalizedProductId,
    reward_label: reward?.reward_label ?? undefined,
    product_name: reward?.product_name ?? undefined,
  };
};

export const buildMissionFormDefaultValues = (
  mission?: Mission,
): MissionSchema => {
  if (!mission) {
    return { ...missionFormDefaultValues };
  }

  return {
    ...missionFormDefaultValues,
    ...mission,
    mission_type: mission.mission_type ?? missionFormDefaultValues.mission_type,
    establishment:
      mission.mission_type === "establishment"
        ? mission.establishment ?? null
        : null,
    allowed_establishments:
      mission.mission_type === "establishment"
        ? mission.allowed_establishments ?? []
        : [],
    conditions: mission.conditions ?? [],
    rewards: (mission.rewards ?? []).map((reward) =>
      mapRewardToFormValue(reward),
    ),
    max_times: mission.max_times ?? null,
  };
};

export const resolveRewardProductId = (
  reward?: { product_id?: string | null; reward_id?: string | null } | null,
) => {
  if (!reward) return null;
  return reward.product_id || reward.reward_id || null;
};

export const mapMissionProvidersToOptions = (
  providers: MissionProvider[],
): Option[] => {
  return providers.map((provider) => ({
    label:
      provider.display_name ||
      provider.provider_klass?.split("::").pop() ||
      provider.provider_klass,
    value: provider.provider_klass,
  }));
};

export const mapMissionProductsToOptions = (products: Product[]): Option[] => {
  return products.map((product) => ({
    label: product.display_name || `Produto ${product.id}`,
    value: String(product.id),
  }));
};
